import { z } from 'zod';
import {
  makeStandardDimensionUnitSchema,
  makeStandardEquipmentTypeSchema,
  makeStandardFreightModeSchema,
  makeStandardShippingUnitSchema,
  makeStandardTemperatureUnitSchema,
  StandardFreightMode,
  StandardShippingUnit,
} from '../enums';
import { formatOption } from '../stringification/options';

export enum FreightDataMappingTypeEnum {
  STANDARD_EQUIPMENT_DETAILS = 'STANDARD_EQUIPMENT_DETAILS',
  ACCESSORIAL_CODE = 'ACCESSORIAL_CODE',
  REFERENCE_NUMBER_TYPE = 'REFERENCE_NUMBER_TYPE',
  SHIPPING_UNIT = 'SHIPPING_UNIT',
  FREIGHT_MODE = 'FREIGHT_MODE',
}

export const FREIGHT_DATA_MAPPING_TYPES = Object.values(
  FreightDataMappingTypeEnum
) as FreightDataMappingTypeEnum[];

export type FreightDataMappingType = FreightDataMappingTypeEnum;

const makeStandardDimensionValueSchema = () =>
  z
    .object({
      value: z.number(),
      units: makeStandardDimensionUnitSchema(),
    })
    .strict();

const makeStandardTemperatureValueSchema = () =>
  z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      units: makeStandardTemperatureUnitSchema().optional(),
    })
    .strict();

export const makeFreightDataMappingStandardEquipmentDetailsSchema = () =>
  z
    .object({
      equipmentType: makeStandardEquipmentTypeSchema().optional(),
      equipmentLength: makeStandardDimensionValueSchema().optional(),
      temperature: makeStandardTemperatureValueSchema().optional(),
      description: z.string().optional(),
    })
    .strict();

export type FreightDataMappingStandardEquipmentDetails = z.infer<
  ReturnType<typeof makeFreightDataMappingStandardEquipmentDetailsSchema>
>;

const freightDataMappingValuesEnvelopeSchema = z
  .object({
    value: z.unknown(),
  })
  .strict();

export type FreightDataMappingValuesStandardEquipmentDetails = {
  value: FreightDataMappingStandardEquipmentDetails[];
};

export type FreightDataMappingValuesAccessorialCode = {
  value: string;
};

export type FreightDataMappingValuesReferenceNumberType = {
  value: string;
};

export type FreightDataMappingValuesShippingUnit = {
  value: StandardShippingUnit;
};

export type FreightDataMappingValuesFreightMode = {
  value: StandardFreightMode;
};

export type FreightDataMappingValues =
  | FreightDataMappingValuesStandardEquipmentDetails
  | FreightDataMappingValuesAccessorialCode
  | FreightDataMappingValuesReferenceNumberType
  | FreightDataMappingValuesShippingUnit
  | FreightDataMappingValuesFreightMode;

export function formatEquipmentDetailsList(
  details: FreightDataMappingStandardEquipmentDetails[]
): string {
  return details
    .map((d) => {
      const parts: string[] = [];
      if (d.equipmentType) parts.push(formatOption(d.equipmentType));
      if (d.equipmentLength)
        parts.push(`${d.equipmentLength.value}${d.equipmentLength.units}`);
      if (d.description) parts.push(d.description);
      return parts.join(' ');
    })
    .filter(Boolean)
    .join(' | ');
}

export const parseFreightDataMappingValues = (
  mappingType: FreightDataMappingType,
  raw: unknown
): FreightDataMappingValues => {
  if (!FREIGHT_DATA_MAPPING_TYPES.includes(mappingType)) {
    throw new Error(`Unknown freight data mapping type: ${mappingType}`);
  }
  const envelope = freightDataMappingValuesEnvelopeSchema.parse(raw);
  switch (mappingType) {
    case FreightDataMappingTypeEnum.STANDARD_EQUIPMENT_DETAILS: {
      const valueSchema = z.array(
        makeFreightDataMappingStandardEquipmentDetailsSchema()
      );
      return { value: valueSchema.parse(envelope.value) };
    }
    case FreightDataMappingTypeEnum.ACCESSORIAL_CODE:
    case FreightDataMappingTypeEnum.REFERENCE_NUMBER_TYPE: {
      return { value: z.string().parse(envelope.value) };
    }
    case FreightDataMappingTypeEnum.SHIPPING_UNIT: {
      return { value: makeStandardShippingUnitSchema().parse(envelope.value) };
    }
    case FreightDataMappingTypeEnum.FREIGHT_MODE: {
      return { value: makeStandardFreightModeSchema().parse(envelope.value) };
    }
  }
};
